import { Button, Flex, Skeleton, Space } from 'antd'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FunctionComponent, ReactNode } from 'react'
import Highlighter from 'react-highlight-words'
import styled from 'styled-components'
import { KOLName } from '@/components/common/kol-name-badge'
import { KolThumbnail } from '@/components/common/kol-thumbnail'
import { Icon } from '@/components/custom-icon'
import HighPotentialTag from '@/components/kol/common/high-potential-tag'
import NumberSummary from '@/components/kol/navigation/number-summary'
import { PlatformData } from '@/components/kol/navigation/platform-select'
import QuickActions from '@/components/kol/navigation/quick-actions'
import { makeKolInfo1 } from '@/constants/mock-data/kol-info'
import { useAuth } from '@/hooks/use-auth'
import { Page } from '@/hooks/use-authorization/constants'
import getPage from '@/hooks/use-authorization/get-page'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'
import { usePermissions } from '@/hooks/use-permissions'
import { useSelector } from '@/hooks/use-selector'
import { useThemeConstants } from '@/hooks/use-theme-constants'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { PlatformCode, PlatformShortcode } from '@/utils/convert-platform'
import { getThumbnail } from '@/utils/get-thumbnails'

export const KOL_DETAIL_TOP_SUMMARY_HEIGHT = 80

interface TopSummaryProps {
  isLoading: boolean
  kolInfo?: KolSchemaModel
  platformData?: PlatformData
  keyword?: string
}

const TopSummary: FunctionComponent<TopSummaryProps> = ({
  isLoading,
  kolInfo,
  platformData,
  keyword,
}) => {
  const { formatMessage } = useIntl()
  const { highlightStyle } = useThemeConstants()
  const { pathname } = useRouter()
  const isPublicDetailPage = useIsPublicDetailPage()
  const { tourModalVisible } = useSelector((state) => state.tour)

  const { isAgent } = useAuth()
  const { withKolPermission, withAgencyKolPermission } = usePermissions()

  const page = getPage(pathname)
  const isKolSelfPage = page
    ? [Page.KolSelf, Page.KolAgencyId].includes(page)
    : false
  const kpi = kolInfo?.kpi.find(
    (kpi) => kpi.platform === platformData?.shortcode,
  )
  const subNames = kolInfo?.subName.join(', ') ?? ''

  const mockKolInfo1 = makeKolInfo1()

  const kolNameSkeleton = (
    <StyledSkeleton active paragraph={{ rows: 1 }} title={{ width: '100%' }} />
  )

  return (
    <Background>
      <HStack>
        <KolThumbnail
          badgeSize={20}
          borderWidth={2}
          imageSize={60}
          isLoading={isLoading}
          isRadarStar={kolInfo?.isRadarStar}
          isVerified={kolInfo?.verified}
          shape='round'
          src={kolInfo?.id ? getThumbnail(kolInfo.id) : undefined}
        />
        {isLoading ? (
          kolNameSkeleton
        ) : (
          <Space direction='vertical' size={5}>
            <KOLName>{kolInfo?.name}</KOLName>
            <Flex gap={5}>
              <HighPotentialTag potential={kolInfo?.potential} />
              {subNames && (
                <SubNames>
                  <Highlighter
                    autoEscape
                    highlightStyle={highlightStyle}
                    searchWords={[keyword ?? '']}
                    textToHighlight={subNames}
                  />
                </SubNames>
              )}
            </Flex>
          </Space>
        )}
        <Spacer />
        <NumberSummary isLoading={isLoading} kpi={kpi} />
        {withKolPermission && !withAgencyKolPermission && (
          <Link
            href={{
              pathname: Page.SettingsKol,
              query: isAgent ? { id: kolInfo?.id } : undefined,
            }}
          >
            <Button type='primary'>
              <Icon type='edit' />
              {formatMessage({ id: 'kol:edit_kol_data_btn' })}
            </Button>
          </Link>
        )}
        {((): ReactNode => {
          if (tourModalVisible) {
            const mockPlatformData = {
              code: PlatformCode.Instagram,
              shortcode: PlatformShortcode.Instagram,
              title: 'Instagram',
              url: '',
            }
            return (
              <QuickActions
                isDisabled={false}
                kolInfo={{ ...mockKolInfo1, visibility: true }}
                platformData={mockPlatformData}
              />
            )
          }

          if (
            isPublicDetailPage ||
            isKolSelfPage ||
            !platformData ||
            !kolInfo
          ) {
            return null
          }

          return (
            <QuickActions
              isDisabled={isLoading}
              kolInfo={kolInfo}
              platformData={platformData}
            />
          )
        })()}
      </HStack>
    </Background>
  )
}

const Background = styled.div`
  height: ${KOL_DETAIL_TOP_SUMMARY_HEIGHT}px;
  border-bottom: 1px solid ${({ theme }): string => theme.colors.border.divider};
`

const HStack = styled.div`
  display: flex;
  align-items: center;
  max-width: 1280px;
  padding: 0 40px;
  margin: 0 auto;
  height: 100%;
  line-height: 100%;

  ${KolThumbnail} {
    margin-right: 15px;
  }

  ${KOLName} {
    color: ${({ theme }): string => theme.colors.text.primary};
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 140%;
    height: auto;
  }
`

const SubNames = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey2};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

const Spacer = styled.div`
  flex: 1 0 10px;
`

const StyledSkeleton = styled(Skeleton)`
  width: 100%;

  .ant-skeleton-content {
    h3.ant-skeleton-title {
      margin: 0 0 10px 0 !important;
    }

    .ant-skeleton-paragraph {
      margin: 0 !important;
    }
  }
`

export default TopSummary
